"use client";

import { FC, useRef, useState } from "react";
import clsx from "clsx";
import { useRouter } from "next/navigation";
import axios from "axios";
import { trimEnd } from "lodash";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";

import TInput from "@/components/atoms/input/TInput";
import TSelect from "@/components/atoms/select/TSelect";
import { leadCaptureSchema } from "@/app/api/validationSchemas";
import GoogleReCaptcha from "@/sections/common/GoogleReCaptcha";
import useGoogleReCaptcha from "@/hooks/useGoogleReCaptcha";
import { handleException, IAxiosError } from "@/utils/exception.helper";
import { markUserAsLead, trackEvent } from "@/utils/analytics";
import { hashEmail } from "@/utils/hash";
import { hasMarketingConsent } from "@/utils/cookie.helper";
import { THANK_YOU_PATH } from "@/constants/conversion";
import { env } from "@/env.mjs";

// ----------------------------------------------------------------------------
// Low-friction lead-capture form for pentest / VAPT landing pages.
//
// Four fields only (name, work email, company, scope) so it converts paid
// traffic above the fold without a page navigation. `intent` toggles the copy
// + the conversion event: "quote" fires quote_request (high intent),
// "sample-report" fires sample_report_request (mid intent). Both also fire
// generate_lead so the existing GA4/Ads conversion keeps working, and both
// send a hashed email for Enhanced Conversions matching — no raw PII ever
// enters the dataLayer or the URL.
// ----------------------------------------------------------------------------

// Pull GA4-derived segmentation from localStorage so the lead lands with
// context (mirrors the contact-us form's readUserProps).
const readUserProps = (): {
  industry_interest?: string;
  service_interest?: string;
  traffic_origin?: string;
  user_type?: string;
} => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage?.getItem("cb_user_props");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const SCOPE_OPTIONS = [
  { value: "web-app", label: "Web application" },
  { value: "mobile-app", label: "Mobile application" },
  { value: "api", label: "API" },
  { value: "network", label: "Network / infrastructure" },
  { value: "cloud", label: "Cloud (AWS / Azure / GCP)" },
  { value: "multiple", label: "Multiple / full-scope" },
  { value: "not-sure", label: "Not sure yet" },
];

export type ILeadCaptureFormProps = {
  intent: "quote" | "sample-report";
  /** Extra classes on the <form>. */
  className?: string;
  /** Called after a successful submit (e.g. to close a modal). */
  onSuccess?: () => void;
  /** Compact spacing for the sticky-bar modal. */
  compact?: boolean;
};

type IValues = {
  name: string;
  workEmail: string;
  company: string;
  scope: string;
  intent: "quote" | "sample-report";
  emailConcent?: boolean;
};

const LeadCaptureForm: FC<ILeadCaptureFormProps> = ({
  intent,
  className,
  onSuccess,
  compact = false,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const router = useRouter();
  const hasStartedRef = useRef(false);
  const formStartedAtRef = useRef<number | null>(null);

  // The form's real DOM id. Rendered into <form id=...> AND sent as the
  // `form_id` analytics param, so the value GA4 receives always matches the
  // element actually on the page — they cannot drift.
  const formDomId = `pentest-lead-form-${intent}`;

  const {
    reCaptchaRef,
    isReCaptchaAvailable,
    isRecaptchaVarified,
    resetReCaptcha,
  } = useGoogleReCaptcha();

  // Explicitly-typed defaultValues + no useForm generic — mirrors the
  // ContactUsForm pattern so the yup-inferred resolver type doesn't clash
  // with the field-values generic.
  const defaultValues: IValues = {
    name: "",
    workEmail: "",
    company: "",
    scope: "",
    intent,
    emailConcent: true,
  };

  const { handleSubmit, reset, control } = useForm({
    defaultValues,
    resolver: yupResolver(leadCaptureSchema),
  });

  const handleFormStart = () => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    formStartedAtRef.current = Date.now();
    trackEvent("form_start", { form_name: `pentest_lead_${intent}` });
  };

  const onSubmit = async (formData: IValues) => {
    const { success, token } = await isRecaptchaVarified();
    if (!success) return;

    try {
      setIsSubmitting(true);
      const props = readUserProps();

      // Hash email client-side for Enhanced Conversions; the raw email still
      // goes to the API for ZeroBounce + Sendy, but the dataLayer event
      // carries only the hash.
      const hashedEmail = await hashEmail(formData.workEmail || "");

      await axios.post(
        `${trimEnd(env.NEXT_PUBLIC_SITE_URL, "/")}/api/lead`,
        {
          name: formData.name,
          workEmail: formData.workEmail,
          company: formData.company,
          scope: formData.scope,
          intent,
          emailConcent: formData.emailConcent,
          industryInterest: props.industry_interest,
          serviceInterest: props.service_interest,
          trafficOrigin: props.traffic_origin,
          userType: props.user_type,
          pageUrl:
            typeof window !== "undefined" ? window.location.href : undefined,
        },
        { headers: token ? { "x-recaptcha-token": token } : undefined },
      );

      const timeToSubmit = formStartedAtRef.current
        ? Math.round((Date.now() - formStartedAtRef.current) / 1000)
        : 0;

      // lead_source distinguishes the two offers this one form serves, so GA4
      // can segment without separate event names. Quote is the high-intent
      // pentest path; sample-report is the softer mid-intent one.
      const leadSource =
        intent === "quote" ? "pentest_quote" : "sample_report";

      const commonParams = {
        lead_source: leadSource,
        // LEGACY (kept for rollback) — form_id previously duplicated
        // lead_source (`pentest_quote` / `sample_report`). Spec requires the
        // form's DOM id, so it now reuses `formDomId`, the same constant
        // rendered into <form id=...> below.
        // form_id: leadSource,
        form_id: formDomId,
        form_name: `pentest_lead_${intent}`,
        intent,
        scope: formData.scope || "unspecified",
        has_company: Boolean(formData.company),
        time_to_submit_s: timeToSubmit,
      } as const;

      // THE conversion event — GA4 key event, imported by Google Ads as the
      // single primary conversion. Fires exactly once, only after the API
      // confirmed success above. The Enhanced-Conversions hash rides ONLY on
      // this event (not the GA4-only companion below — data minimization), and
      // only with Marketing consent for the advertising use of PII; without it
      // the key is omitted so no email-derived value reaches the dataLayer.
      trackEvent("generate_lead", {
        ...commonParams,
        sha256_email_address:
          hasMarketingConsent() && hashedEmail ? hashedEmail : undefined,
      });
      // GA4-only segmentation signal. Do NOT build a Google Ads tag on this —
      // it fires alongside generate_lead and would double-count.
      trackEvent(
        intent === "quote" ? "quote_request" : "sample_report_request",
        commonParams,
      );
      markUserAsLead();

      reset();
      // Close the modal (when rendered inside StickyLeadBar) before navigating
      // so it doesn't flash over the thank-you page during the transition.
      onSuccess?.();

      // Same canonical thank-you destination as the contact form — one page,
      // one Google Ads page-load trigger, covering every lead form on the
      // site. See src/constants/conversion.ts.
      router.push(THANK_YOU_PATH);
    } catch (error) {
      handleException(error as IAxiosError);
    } finally {
      setIsSubmitting(false);
      resetReCaptcha();
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      onFocus={handleFormStart}
      onChange={handleFormStart}
      id={formDomId}
      className={clsx(
        "flex w-full flex-col",
        compact ? "gap-3" : "gap-4",
        className,
      )}
    >
      <TInput name="name" control={control} placeholder="Full name *" />
      <TInput name="workEmail" control={control} placeholder="Work email *" />
      <TInput name="company" control={control} placeholder="Company *" />
      <TSelect
        name="scope"
        control={control}
        placeholder="What needs testing? *"
        data={SCOPE_OPTIONS}
      />

      {isReCaptchaAvailable && <GoogleReCaptcha ref={reCaptchaRef} />}

      <button
        type="submit"
        id={`pentest-lead-submit-${intent}`}
        disabled={isSubmitting}
        className={clsx(
          "group inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#FB441B] px-6 font-bricolage-grotesque text-base font-medium text-white shadow-sm transition-all hover:bg-[#E03A17] hover:shadow-[0_10px_25px_rgba(251,68,27,0.3)]",
          isSubmitting && "!cursor-not-allowed !opacity-70",
        )}
      >
        {isSubmitting
          ? "Submitting…"
          : intent === "quote"
            ? "Get my pentest quote"
            : "Send me the sample report"}
      </button>

      <p className="text-center font-darker-grotesque text-xs font-normal text-black-500">
        No spam. A real CERT-In empanelled engineer replies, not a bot.
      </p>
    </form>
  );
};

export default LeadCaptureForm;
